/* coldnumbers — Google Analytics 4 event tracking
 * ---------------------------------------------------------------------------
 * Loaded on every page right after the GA4 tag in <head>. The tag itself
 * records page views, sessions, engagement time, traffic source, device and
 * location, and tags every hit with a content_group (Home, Analyzer, State
 * pages, …) so pages can be grouped in reports. This file adds what people DO:
 *
 *   affiliate_click   click on a partner link (/go/<partner>, rel="sponsored" or
 *                     data-affiliate). partner, placement, link_text, link_url
 *   link_click        any other link. link_text, link_url, link_domain,
 *                     outbound, link_type, section
 *   share             social share links. method, content_type, item_id
 *   button_click      buttons, tabs, toggles, anything with onclick.
 *                     button_text, button_id, section
 *   tool_setting      a dropdown / slider / checkbox / date changed.
 *                     setting, value, section   (typed text is never sent)
 *   <tool events>     a person ran a tool: analyze_powerball, calc_tax,
 *                     wheel_spin, … (see TOOL_FUNCTIONS and USER_ONLY below
 *                     and the track() calls in main.js). Runs the page does
 *                     on its own (on load, data refresh) are not counted.
 *   scroll_depth      25 / 50 / 75 / 100 % of the page. percent_scrolled
 *   exception         a JavaScript error on the page (max 5 per page).
 *
 * "section" says where on the page the click happened: the card title
 * (e.g. "Weather inputs"), or nav / footer / affiliate_grid / an element id.
 * Add data-track-section="..." to any container, or data-track-label="..."
 * to any element, to override the automatic names.
 *
 * Owner tools (no code changes needed):
 *   ?cn_ga=off    stop counting this browser (remembered) — use on your own devices
 *   ?cn_ga=on     count it again
 *   ?cn_ga=debug  send this browser's hits to GA4 → Admin → DebugView
 * (handled by the inline tag in each page's <head>, before anything is sent)
 */
(function () {
  'use strict';
  if (window.__cnAnalytics) return;
  window.__cnAnalytics = true;

  // ── basics ────────────────────────────────────────────────────────────────
  function clip(s, n) {
    n = n || 100; // GA4 limit for parameter values
    s = String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
  }
  function send(name, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (e) {}
  }

  // When did the visitor last click, type or press a key? Lets tool events say
  // whether a person ran the tool or the page ran it on its own.
  var lastUserAction = 0;
  ['pointerdown', 'keydown', 'input', 'change', 'submit', 'touchstart'].forEach(function (t) {
    document.addEventListener(t, function () { lastUserAction = Date.now(); }, true);
  });
  function userDriven() { return Date.now() - lastUserAction < 3000; }

  // Calculators recalculate on every keystroke; send one event per burst.
  var pending = {};
  function sendSoon(key, name, params, wait) {
    if (pending[key]) clearTimeout(pending[key].t);
    pending[key] = { name: name, params: params,
      t: setTimeout(function () { delete pending[key]; send(name, params); }, wait || 1200) };
  }
  function flush() {
    Object.keys(pending).forEach(function (k) { clearTimeout(pending[k].t); send(pending[k].name, pending[k].params); });
    pending = {};
  }
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });
  window.addEventListener('pagehide', flush);

  // Tool events that fire on every change of an input → coalesced.
  var BURSTY = {
    calc_tax: 1, calc_lump_vs_annuity: 1, calc_compound: 1, calc_quick_tax: 1,
    calc_state_tax: 1, analyze_powerball: 1, analyze_megamillions: 1,
    group_budget_calc: 1, group_calc_win: 1
  };
  // Tools the page also runs by itself (on load, when data arrives, when the
  // forecast refreshes). Those automatic runs are not people using the tool,
  // so these events are only sent within a few seconds of a click/keypress.
  var USER_ONLY = {
    analyze_powerball: 1, analyze_megamillions: 1, calc_tax: 1, calc_lump_vs_annuity: 1,
    calc_compound: 1, calc_quick_tax: 1, rng_generate: 1
  };

  // Public helper used by main.js and inline page scripts: track(name, params)
  function track(name, params) {
    params = params || {};
    if (USER_ONLY[name] && !userDriven()) return;
    if (BURSTY[name]) sendSoon(name, name, params);
    else send(name, params);
  }
  window.track = track;
  window.cnTrack = track;

  // ── where on the page? ───────────────────────────────────────────────────
  function sectionOf(el) {
    var tagged = el.closest('[data-track-section]');
    if (tagged) return clip(tagged.getAttribute('data-track-section'), 60);
    if (el.closest('.aff-grid')) return 'affiliate_grid';
    if (el.closest('.a11y-panel')) return 'accessibility_panel';
    if (el.closest('nav, .nav-links, .nav-more-menu, .nav-drop-wrap, header, .topbar')) return 'nav';
    if (el.closest('footer')) return 'footer';
    var card = el.closest('.card');
    if (card) {
      var hd = card.querySelector('.card-hd, .card-title, h2, h3');
      var title = hd && clip(hd.innerText || hd.textContent, 60);
      if (title) return 'card: ' + title;
    }
    var box = el.parentElement && el.parentElement.closest('[id]');
    while (box && /^(main-content|root|app)$/.test(box.id)) box = box.parentElement && box.parentElement.closest('[id]');
    if (box) return '#' + clip(box.id, 59);
    var sec = el.closest('section, aside, form, main');
    return sec ? sec.tagName.toLowerCase() : 'page';
  }
  function labelOf(el) {
    var t = el.getAttribute('data-track-label') || el.getAttribute('aria-label');
    if (!t && el.tagName === 'INPUT') t = el.value || el.name;
    if (!t) t = el.innerText || el.textContent;
    if (!t) { var img = el.querySelector && el.querySelector('img[alt]'); if (img) t = img.alt; }
    return clip(t || el.getAttribute('title') || el.id || el.tagName.toLowerCase());
  }

  // ── affiliate partners ───────────────────────────────────────────────────
  function partnerOf(a, url) {
    if (a.getAttribute('data-affiliate')) return a.getAttribute('data-affiliate');
    var m = url.pathname.match(/\/go\/([a-z0-9_-]+?)(?:\.html)?\/?$/i);
    return m ? m[1].toLowerCase() : url.hostname.replace(/^www\./, '');
  }
  function isAffiliate(a, url) {
    return a.hasAttribute('data-affiliate') ||
      (url.host === location.host && /\/go\/[a-z0-9_-]+/i.test(url.pathname)) ||
      /\bsponsored\b/i.test(a.getAttribute('rel') || '');
  }
  var SHARE = [
    [/(^|\.)twitter\.com$|(^|\.)x\.com$/, 'x'], [/facebook\.com$/, 'facebook'],
    [/reddit\.com$/, 'reddit'], [/linkedin\.com$/, 'linkedin'], [/wa\.me$|whatsapp\.com$/, 'whatsapp']
  ];
  function shareMethod(url) {
    if (!/intent|share|submit|sharer/i.test(url.pathname + url.search)) return null;
    for (var i = 0; i < SHARE.length; i++) if (SHARE[i][0].test(url.hostname)) return SHARE[i][1];
    return null;
  }

  // ── clicks ───────────────────────────────────────────────────────────────
  var CLICKABLE = 'a[href], button, [role="button"], [role="tab"], [role="menuitem"], [role="switch"], ' +
    'input[type="button"], input[type="submit"], input[type="reset"], input[type="checkbox"], input[type="radio"], ' +
    'summary, [onclick], [data-track]';

  function onClick(e) {
    if (e.type === 'auxclick' && e.button !== 1) return; // middle-click only
    var el = e.target && e.target.closest && e.target.closest(CLICKABLE);
    if (!el) return;
    try {
      var section = sectionOf(el);
      var href = el.tagName === 'A' ? el.getAttribute('href') : null;

      if (href && !/^(#|javascript:)/i.test(href)) {
        var url = new URL(el.href, location.href);

        if (isAffiliate(el, url)) {
          var partner = partnerOf(el, url);
          var pl = el.closest('[data-placement]');
          var placement = pl ? pl.getAttribute('data-placement') : section;
          send('affiliate_click', {
            partner: partner, placement: clip(placement), section: section,
            link_text: labelOf(el), link_url: clip(url.href, 100)
          });
          // Tell the /go/ redirect page where the click came from.
          if (url.host === location.host && /\/go\//.test(url.pathname) && !url.searchParams.has('from')) {
            url.searchParams.set('from', location.pathname);
            url.searchParams.set('placement', clip(placement, 60));
            el.href = url.href;
          }
          return;
        }

        var method = shareMethod(url);
        if (method) {
          send('share', { method: method, content_type: 'page', item_id: clip(location.pathname), section: section });
          return;
        }

        var outbound = url.host !== location.host;
        var type = /^mailto:/i.test(href) ? 'mailto' : /^tel:/i.test(href) ? 'tel'
          : /\.(pdf|csv|xlsx?|zip|json|png|jpe?g|svg)$/i.test(url.pathname) ? 'download'
          : outbound ? 'outbound' : 'internal';
        send('link_click', {
          link_text: labelOf(el), link_url: clip(type === 'mailto' || type === 'tel' ? href.split(':')[0] + ':' : url.href),
          link_domain: url.hostname, outbound: outbound, link_type: type, section: section
        });
        return;
      }

      send('button_click', {
        button_text: labelOf(el),
        button_id: clip(el.id || el.getAttribute('name') || '', 60),
        section: section
      });
    } catch (err) {}
  }
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);

  // ── settings changed (dropdowns, sliders, checkboxes, dates) ─────────────
  // Only non-text controls; nothing a visitor types (numbers, email, notes) is sent.
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.tagName) return;
    var type = (el.getAttribute('type') || '').toLowerCase();
    var ok = el.tagName === 'SELECT' || /^(range|checkbox|radio|date|month|week)$/.test(type);
    if (!ok) return;
    var lab = el.getAttribute('data-track-label') || el.getAttribute('aria-label') ||
      (el.id && document.querySelector('label[for="' + el.id + '"]') || {}).innerText || el.name || el.id || type;
    var value = type === 'checkbox' ? String(el.checked)
      : el.tagName === 'SELECT' ? (el.options[el.selectedIndex] || {}).text || el.value : el.value;
    sendSoon('set:' + (el.id || el.name || lab), 'tool_setting',
      { setting: clip(lab, 60), value: clip(value, 60), section: sectionOf(el) }, 800);
  }, true);

  // ── tool runs that main.js / page scripts don't report themselves ────────
  // name of the global function → [GA4 event, extra params]. Only counted when
  // a person triggered it (so automatic re-renders don't inflate the numbers).
  function stateSlug() { var m = location.pathname.match(/states\/([a-z-]+)/); return m ? m[1] : ''; }
  // main.js keeps some state in top-level `let` variables: readable by name
  // from this script, but not as window properties.
  function scanGame() { /* global _scanGame */ return typeof _scanGame !== 'undefined' ? (_scanGame === 'mm' ? 'megamillions' : 'powerball') : ''; }
  function ocrMode()  { /* global _ocrMode */  return typeof _ocrMode  !== 'undefined' ? _ocrMode : ''; }
  var TOOL_FUNCTIONS = {
    doCalc:               ['calc_state_tax',      function () { return { state: stateSlug() }; }],
    setPayMode:           ['calc_state_tax',      function (a) { return { state: stateSlug(), pay_mode: a[0] }; }],
    checkAllManual:       ['check_tickets',       function () { return { game: scanGame() }; }],
    handleScan:           ['scan_upload',         function () { return { ocr_mode: ocrMode(), game: scanGame() }; }],
    testRngVsHistory:     ['rng_test_history',    null],
    saveRngResults:       ['save_random_picks',   null],
    findPairs:            ['find_pairs',          null],
    computeMathStats:     ['math_stats',          null],
    runBacktest:          ['dashboard_backtest',  null],
    generate:             ['dashboard_generate',  null],
    autoPBWeights:        ['auto_weights',        function () { return { game: 'powerball' }; }],
    autoMMWeights:        ['auto_weights',        function () { return { game: 'megamillions' }; }],
    savePBWeightPreset:   ['save_weight_preset',  function () { return { game: 'powerball' }; }],
    saveMMWeightPreset:   ['save_weight_preset',  function () { return { game: 'megamillions' }; }],
    applyPBPreset:        ['analyzer_preset',     function (a) { return { game: 'powerball', preset: clip(a[0], 40) }; }],
    applyMMPreset:        ['analyzer_preset',     function (a) { return { game: 'megamillions', preset: clip(a[0], 40) }; }],
    selectTier:           ['wheel_tier',          function (a) { return { tier: clip(a[0], 20) }; }],
    authSendMagicLink:    ['login_request',       function () { return { method: 'magic_link' }; }],
    authSignOut:          ['logout',              null],
    gpNewPool:            ['group_pool_new',      null],
    gpSaveSetup:          ['group_pool_save',     null],
    gpAddMember:          ['group_member_add',    null],
    gpPrintAgreement:     ['group_agreement',     function () { return { action: 'print' }; }],
    gpEmailAgreement:     ['group_agreement',     function () { return { action: 'email' }; }],
    gpPrintCalendar:      ['group_calendar_print', null],
    gpCheckResults:       ['group_check_results', null],
    gpCalcWin:            ['group_calc_win',      null],
    gpVerifyChain:        ['group_verify_chain',  null],
    gpCalcDrawsCovered:   ['group_budget_calc',   null],
    gpCalcAmountFromDraws:['group_budget_calc',   null],
    loadMoreWinners:      ['winners_load_more',   null],
    loadResultsMore:      ['results_load_more',   null]
  };
  function wrapTools() {
    Object.keys(TOOL_FUNCTIONS).forEach(function (fn) {
      var orig = window[fn];
      if (typeof orig !== 'function' || orig.__cnWrapped) return;
      var spec = TOOL_FUNCTIONS[fn];
      var wrapped = function () {
        if (userDriven()) {
          try {
            var p = spec[1] ? spec[1](Array.prototype.slice.call(arguments)) : {};
            if (BURSTY[spec[0]]) sendSoon(spec[0], spec[0], p); else send(spec[0], p);
          } catch (e) {}
        }
        return orig.apply(this, arguments);
      };
      wrapped.__cnWrapped = true;
      try { window[fn] = wrapped; } catch (e) {}
    });
  }
  // main.js and inline page scripts define these as globals; wrap once they exist.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wrapTools);
  else wrapTools();
  window.addEventListener('load', wrapTools);

  // ── scroll depth ─────────────────────────────────────────────────────────
  var marks = [25, 50, 75, 100], sent = {}, ticking = false;
  function checkScroll() {
    ticking = false;
    var doc = document.documentElement;
    var h = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0) - window.innerHeight;
    if (h < 200) return; // page barely scrolls
    var pct = Math.min(100, (window.scrollY || doc.scrollTop) / h * 100 + 2);
    marks.forEach(function (m) {
      if (pct >= m && !sent[m]) { sent[m] = 1; send('scroll_depth', { percent_scrolled: m }); }
    });
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; setTimeout(checkScroll, 250); }
  }, { passive: true });

  // ── JavaScript errors ────────────────────────────────────────────────────
  var errCount = 0;
  function reportError(msg, src, line) {
    if (errCount >= 5) return;
    if (src && !/^https?:/.test(src)) return;                   // browser extensions
    if (src && src.indexOf(location.host) < 0 && !/^Script error/.test(msg)) return; // third-party scripts
    errCount++;
    var file = src ? src.replace(/^https?:\/\/[^/]+/, '').split('?')[0] : '';
    send('exception', { description: clip(msg + (file ? ' @ ' + file + ':' + (line || 0) : '')), fatal: false });
  }
  window.addEventListener('error', function (e) {
    if (e && e.message) reportError(e.message, e.filename, e.lineno);
  });
  window.addEventListener('unhandledrejection', function (e) {
    var r = e && e.reason;
    reportError('Unhandled promise: ' + (r && (r.message || r)), location.href, 0);
  });
})();
